import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { DatePicker } from "@/components/ui/date-picker";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ChevronsUpDown } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";
import { useAcknowledgeStockEntry } from "../../hooks/useSowingLifecycle";
import { useGreenhouses, useGreenhouseBeds } from "@/features/farms/hooks/useGreenhouses";
import { useBaysByGreenhouseId } from "@/features/farms/hooks/useGreenhouseBays";
import type { StockEntryPipelineDto } from "../../services/sowingLifecycleApi";

interface Props {
  entry: StockEntryPipelineDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AcknowledgeStockEntryDialog = ({ entry, open, onOpenChange, onSuccess }: Props) => {
  const notify = useNotification();
  const mutation = useAcknowledgeStockEntry();
  const [greenhouseId, setGreenhouseId] = useState("");
  const [selectedBayIds, setSelectedBayIds] = useState<string[]>([]);
  const [bedAllocations, setBedAllocations] = useState<Map<string, number>>(new Map());
  const [expectedReadyDate, setExpectedReadyDate] = useState("");
  const [notes, setNotes] = useState("");
  const [bayPopoverOpen, setBayPopoverOpen] = useState(false);
  const [bedPopoverOpen, setBedPopoverOpen] = useState(false);

  const greenhousesQuery = useGreenhouses();
  const baysQuery = useBaysByGreenhouseId(greenhouseId);
  const bedsQuery = useGreenhouseBeds(greenhouseId);
  const bedsInSelectedBays = (bedsQuery.data ?? []).filter((b) => selectedBayIds.includes(b.bayId));

  const expectedTotal = Math.floor(entry.totalQuantity + entry.fillingReserve);
  const allocatedTotal = Array.from(bedAllocations.values()).reduce((a, b) => a + b, 0);
  const hasZeroValues = Array.from(bedAllocations.values()).some((c) => c <= 0);
  const allocationsValid = bedAllocations.size === 0
    ? !entry.requiresBedAllocation
    : !hasZeroValues && allocatedTotal === expectedTotal;
  const allocationDiff = allocatedTotal - expectedTotal;

  const handleGreenhouseChange = (gid: string) => {
    setGreenhouseId(gid);
    setSelectedBayIds([]);
    setBedAllocations(new Map());
  };
  const toggleBay = (bayId: string) => {
    setSelectedBayIds((prev) => {
      const next = prev.includes(bayId) ? prev.filter((x) => x !== bayId) : [...prev, bayId];
      setBedAllocations((current) => {
        const updated = new Map(current);
        for (const bedId of Array.from(updated.keys())) {
          const bed = bedsQuery.data?.find((b) => b.id === bedId);
          if (!bed || !next.includes(bed.bayId)) updated.delete(bedId);
        }
        return updated;
      });
      return next;
    });
  };
  const toggleBed = (bedId: string) =>
    setBedAllocations((prev) => {
      const next = new Map(prev);
      if (next.has(bedId)) next.delete(bedId);
      else next.set(bedId, 0);
      return next;
    });
  const setBedTrayCount = (bedId: string, value: string) => {
    const n = Math.max(0, parseInt(value || "0", 10) || 0);
    setBedAllocations((prev) => {
      const next = new Map(prev);
      next.set(bedId, n);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!greenhouseId) {
      notify.error("Select a greenhouse");
      return;
    }
    if (bedAllocations.size === 0 && entry.requiresBedAllocation) {
      notify.error("Assign the trays to at least one bed");
      return;
    }
    if (bedAllocations.size > 0) {
      if (Array.from(bedAllocations.values()).some((c) => c <= 0)) {
        notify.error("Each selected bed must have a tray count greater than zero");
        return;
      }
      if (allocatedTotal !== expectedTotal) {
        notify.error(`Per-bed trays must total ${expectedTotal} (currently ${allocatedTotal})`);
        return;
      }
    }
    mutation.mutate(
      {
        entryId: entry.id,
        payload: {
          greenhouseId,
          bedAllocations: Array.from(bedAllocations.entries()).map(([bedId, trayCount]) => ({ bedId, trayCount })),
          expectedReadyDate: expectedReadyDate
            ? new Date(expectedReadyDate).toISOString()
            : undefined,
          notes: notes || undefined,
        },
      },
      {
        onSuccess: () => {
          notify.success("Stock entry acknowledged");
          onSuccess();
        },
        onError: (err: any) =>
          notify.error(err.response?.data?.message ?? "Failed to acknowledge"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Acknowledge Stock Entry</DialogTitle>
          <DialogDescription>
            {entry.instructionNumber} — {entry.cropTypeName}
            {entry.variety ? ` · ${entry.variety}` : ""} ({entry.availableQuantity} available · {expectedTotal} total trays)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>
              Greenhouse <span className="text-destructive">*</span>
            </FieldLabel>
            <select
              value={greenhouseId}
              onChange={(e) => handleGreenhouseChange(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              disabled={mutation.isPending}
            >
              <option value="">Select greenhouse…</option>
              {greenhousesQuery.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.greenhouseName}
                </option>
              ))}
            </select>
          </Field>

          {greenhouseId && (
            <Field>
              <FieldLabel>Bays</FieldLabel>
              <Popover open={bayPopoverOpen} onOpenChange={setBayPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    <span className="truncate">
                      {selectedBayIds.length === 0
                        ? "Select bays…"
                        : `${selectedBayIds.length} bay${selectedBayIds.length !== 1 ? "s" : ""} selected`}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                    {baysQuery.isLoading ? (
                      <p className="text-xs text-muted-foreground p-2">Loading bays…</p>
                    ) : !baysQuery.data || baysQuery.data.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">No bays in this greenhouse.</p>
                    ) : (
                      baysQuery.data.map((bay: { id: string; bayName: string }) => (
                        <label
                          key={bay.id}
                          className="flex items-center gap-2 text-sm cursor-pointer rounded px-2 py-1 hover:bg-muted"
                        >
                          <input
                            type="checkbox"
                            checked={selectedBayIds.includes(bay.id)}
                            onChange={() => toggleBay(bay.id)}
                          />
                          <span>{bay.bayName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </Field>
          )}

          {selectedBayIds.length > 0 && (
            <Field>
              <FieldLabel>Beds</FieldLabel>
              <Popover open={bedPopoverOpen} onOpenChange={setBedPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    <span className="truncate">
                      {bedAllocations.size === 0
                        ? "Assign trays to beds…"
                        : `${bedAllocations.size} bed${bedAllocations.size !== 1 ? "s" : ""} — ${allocatedTotal}/${expectedTotal}`}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <div className="max-h-72 overflow-y-auto p-2 space-y-1">
                    {bedsQuery.isLoading ? (
                      <p className="text-xs text-muted-foreground p-2">Loading beds…</p>
                    ) : bedsInSelectedBays.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">
                        No beds in the selected bay(s).
                      </p>
                    ) : (
                      bedsInSelectedBays.map((bed) => {
                        const isSelected = bedAllocations.has(bed.id);
                        const allocation = bedAllocations.get(bed.id) ?? 0;
                        return (
                          <div
                            key={bed.id}
                            className="flex items-center justify-between gap-2 text-sm rounded px-2 py-1 hover:bg-muted"
                          >
                            <span className="flex items-center gap-2 flex-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleBed(bed.id)}
                              />
                              <span>
                                <span className="text-muted-foreground">{bed.bayName} /</span>{" "}
                                {bed.bedNumber}
                                {bed.currentOccupancy > 0 && (
                                  <span className="ml-1 text-xs text-muted-foreground">
                                    {bed.currentOccupancy} trays on bed
                                  </span>
                                )}
                              </span>
                            </span>
                            {isSelected && (
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                placeholder="trays"
                                value={allocation || ""}
                                onChange={(e) => setBedTrayCount(bed.id, e.target.value)}
                                className="h-7 w-20 text-sm"
                              />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {bedAllocations.size > 0 && (
                <p className={`mt-1 text-xs break-words ${allocationsValid ? "text-muted-foreground" : "text-destructive"}`}>
                  Allocated {allocatedTotal} / {expectedTotal} trays
                  {allocationDiff > 0 && ` — over by ${allocationDiff}, reduce a bed's count`}
                  {allocationDiff < 0 && ` — need ${-allocationDiff} more tray${-allocationDiff === 1 ? "" : "s"}`}
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Assign every tray to a bed. Beds have no size limit — enter what is physically placed.
              </p>
            </Field>
          )}

          <Field>
            <FieldLabel>Expected Ready Date</FieldLabel>
            <DatePicker
              value={expectedReadyDate}
              onChange={(v) => setExpectedReadyDate(v)}
              disabled={mutation.isPending}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Optional — when you expect the plants to be ready for dispatch.
            </p>
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Placement notes (optional)"
              disabled={mutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={mutation.isPending || !allocationsValid}>
              {mutation.isPending ? "Saving…" : "Acknowledge"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
